import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Support = () => {
  const [selectedFaq, setSelectedFaq] = useState(null);


  const faqs = [
    {
      question: 'How do I track my project progress?',
      answer: 'Navigate to the Timeline section to see all project phases, milestones, and overall progress. You can click on any phase to view detailed milestone information.'
    },
    {
      question: 'How can I upload documents?',
      answer: 'Go to the Documents section and use the file upload area to drag and drop or click to select files from your computer. All uploaded files will be instantly available to your project team.'
    },
    {
      question: 'How do I provide feedback on deliverables?',
      answer: 'Visit the Feedback & Approvals section where you can review deliverables, approve them, or request revisions with detailed comments.'
    },
    {
      question: 'What should I do if I find a bug?',
      answer: 'Use the Issue Tracking section to report any bugs. Select "Bug" as the type, choose the priority level, and provide a detailed description. Our team will be notified immediately.'
    },
    {
      question: 'How do I communicate with the AppTek team?',
      answer: 'Use the Communication section for direct messaging with your project team. Messages are delivered in real-time and you can see when they\'ve been read.'
    },
    {
      question: 'Where can I view my invoices?',
      answer: 'All invoices and payment history are available in the Billing & Invoices section. You can download invoices and make payments directly from there.'
    },
    {
      question: 'How often is the timeline updated?',
      answer: 'Your project timeline is updated in real-time as your team completes tasks and milestones. You\'ll receive notifications for major updates.'
    },
    {
      question: 'Can I request additional features?',
      answer: 'Yes! Go to Issue Tracking and create a new issue with the type "Feature Request". Describe your idea and our team will review it.'
    }
  ];



  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Support Center</h1>
        <p className="text-gray-600">Find answers to common questions or get in touch with us.</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-2xl p-8 border border-gray-100 h-fit">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>

            <div className="space-y-6">
              <a href="mailto:support@apptek.dev" className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-all group">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white group-hover:bg-gray-800 transition-colors flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Email Support</p>
                  <p className="text-sm text-gray-600">support@apptek.dev</p>
                </div>
              </a>

              <a href="tel:+4553840989" className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-all group">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white group-hover:bg-gray-800 transition-colors flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Phone Support</p>
                  <p className="text-sm text-gray-600">+45 53 84 09 89</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Business Hours</p>
                  <p className="text-sm text-gray-600">
                    Monday - Friday, 10:00 - 18:00.<br />
                    Feel free to call anytime; we'll do our best to answer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ and Contact */}
        <div className="lg:col-span-2 space-y-8">
          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setSelectedFaq(selectedFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-600 transition-transform ${selectedFaq === index ? 'transform rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {selectedFaq === index && (
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>


        </div>
      </div>
    </div>
  );
};

export default Support;
